import React from "react";

import {
  AdditionalItemAttributeConfig,
  AdditionalItemAttributeTransformTypes,
  ItemDisplay,
  RetailerTranslations,
} from "@narvar/nth-hook-loma";
import {
  AdditionalProperty,
  Maybe,
  useLanguage,
} from "@narvar/nth-kit-returns-headless";
import {
  ExchangeVariant,
  ExtendedOrderItem,
  ExtendedReturnItem,
  ExtendedReturnItemInput,
  ReturnReasonsReturnType,
} from "../ReturnReasons/types";
import {
  Currency,
  formatCurrency,
} from "../ReviewSummary/transformers/Currency";
import {
  AdditionalItemAttrs,
  Attribute,
  ExtendedSubmittedReturn,
  OfflineReturnsHandlingType,
} from "./types";

export const DEFAULT_ITEM_ATTR_CONFIG: string[] = [
  "quantity",
  "variantInfo",
  "color",
  "size",
  "sku",
  "price",
];

export const filterItems = (
  items: ExtendedOrderItem[],
  submittedReturns: ExtendedSubmittedReturn[] = [],
  showOfflineReturnsAs: OfflineReturnsHandlingType = "hidden",
): [ExtendedOrderItem[], ExtendedOrderItem[]] => {
  const eligible: ExtendedOrderItem[] = [];
  const ineligible: ExtendedOrderItem[] = [];
  const hasOfflineReturnItem = (item: ExtendedOrderItem) => {
    const onlineReturnedQuantity = submittedReturns
      .map((r) => r.returnShipments ?? [])
      .flat()
      .map((s) => s.items)
      .flat()
      .reduce(
        (sum, submittedReturnItem) =>
          submittedReturnItem.itemId === item.itemId
            ? sum + submittedReturnItem.quantity
            : sum,
        0,
      );
    return (
      item.orderedQuantity - onlineReturnedQuantity > item.remainingQuantity
    );
  };

  items.forEach((item) => {
    const eligibility = item.eligibilities;
    if (
      (eligibility?.return?.isEligible || eligibility?.exchange?.isEligible) &&
      item.remainingQuantity > 0
    ) {
      eligible.push(item);
    } else if (
      item.remainingQuantity > 0 ||
      (showOfflineReturnsAs === "ineligible" && hasOfflineReturnItem(item))
    ) {
      ineligible.push(item);
    }
  });

  return [eligible, ineligible];
};

export const splitItemsForMixReturns = (
  items?: ExtendedOrderItem[],
  selectedItems?: ExtendedReturnItemInput[],
): ExtendedOrderItem[] => {
  const splitItems: ExtendedOrderItem[] = (items ?? []).reduce(
    (ary: ExtendedOrderItem[], item: ExtendedOrderItem) => {
      // split orderItems into multiple rows base on current selections
      let split: ExtendedOrderItem[] = (selectedItems ?? [])
        .filter(
          (itemInput) =>
            itemInput.itemId === item.itemId && itemInput.sku === item.sku,
        )
        .map((itemInput) => ({
          ...item,
          localId: itemInput.localId,
          remainingQuantity: itemInput.quantity,
        }));

      // max localId of current selected item, return -1 if it was unselected
      const maxLocalId: number = Math.max(
        -1,
        ...split.map((item) => item.localId ?? 0),
      );
      const selectedQty: number = split.reduce(
        (sum, item) => sum + item.remainingQuantity,
        0,
      );
      const totalQty: number = item.remainingQuantity;
      const remainingQuantity = totalQty - selectedQty;

      // append another row if there is still any remaining quantity.
      if (remainingQuantity > 0) {
        split = [
          ...split,
          {
            ...item,
            localId: maxLocalId + 1, // last remaining item's localId is always max ID+1, so even if a selected item was removed, the ID won't mixup.
            remainingQuantity,
          },
        ];
      }

      return [...ary, ...split];
    },
    [],
  );

  return splitItems;
};

export const getAdditionalItemAttrs = (
  additionalProperties?: Maybe<AdditionalProperty[]>,
) => {
  if (!additionalProperties) {
    return null;
  }

  /**
   * Additional Properties come in a different format than what we need for getItemAttrs.
   * We convert these to an object of objects, formatting key, and label.
   */
  return additionalProperties.reduce((acc, attr: AdditionalProperty) => {
    const { name, value } = attr;

    // First we split the name into an array
    const attrNameArray = name.split("_");

    // Name attr is formatted to a label.  e.g. `Rx_number` to `Rx number`
    const joinedName = attrNameArray.join(" ");
    const label = `${joinedName.charAt(0).toUpperCase()}${joinedName.slice(1)}`;

    // A key is formatted to snake case. e.g. `Rx_number` to `rxNumber`
    const key =
      attrNameArray.length === 1
        ? `${name.charAt(0).toLowerCase()}${name.slice(1)}`
        : attrNameArray
            .map((word: string, i: number) => {
              let str = word.toLowerCase();

              if (i !== 0) {
                str = `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
              }

              return str;
            })
            .join("");

    acc[key] = {
      label,
      value,
    };

    return acc;
  }, {} as AdditionalItemAttrs);
};

const getTransformedAttribute = (
  transformType: AdditionalItemAttributeTransformTypes,
  {
    key,
    additionalItemAttrs,
    additionalItemAttributeConfig,
    currency,
    includeCurrency,
    locale,
  }: {
    key: string;
    additionalItemAttrs: AdditionalItemAttrs;
    additionalItemAttributeConfig?: Maybe<AdditionalItemAttributeConfig[]>;
    currency?: string;
    includeCurrency?: boolean;
    locale?: string;
  },
): string => {
  const value = additionalItemAttrs?.[key]?.value;

  if (!value) {
    return "";
  }

  // If there is no transform, we return the value as is.
  if (transformType === "none") {
    return value;
  }

  // If thee transform is to concatenate, we need to find the additional attribute config for the concat key,
  // then concat it as a prefix or suffix. Usually, the concatenated attribute will be excluded from display in itemAttributeConfig.
  if (transformType === "toConcatenate") {
    if (!additionalItemAttributeConfig) {
      return value;
    }

    const thisAdditionalItemAttributeConfig =
      additionalItemAttributeConfig?.find((item) => item.attribute === key);
    const thisProperty = thisAdditionalItemAttributeConfig?.value;
    const concatenatedAttributeConfig = additionalItemAttributeConfig?.find(
      (item) => item.attribute === thisProperty?.concatKey,
    );
    const transformedConcatValue: string = getTransformedAttribute(
      concatenatedAttributeConfig?.value?.transform ?? "none",
      {
        key: thisProperty?.concatKey ?? "",
        additionalItemAttrs,
        additionalItemAttributeConfig,
        currency,
        locale,
      },
    );

    return thisProperty?.concatPosition === "suffix"
      ? `${value}${transformedConcatValue}`
      : `${transformedConcatValue}${value}`;
  }

  // If the transform is to currency, we use the Intl.NumberFormat to format the value as currency.
  if (transformType === "toCurrency") {
    return formatCurrency({
      value: parseFloat(value),
      currency,
      includeCurrency,
      locale,
      needsExponent: !value?.toString().includes("."),
    });
  }

  // If the transform is to lowercase, we return the value in lowercase.
  if (transformType === "toLowercase") {
    return value.toLowerCase();
  }

  // Otherwise, we simply return the value as is.
  return value;
};

export const getItemAttrs = ({
  item,
  itemDisplay,
  selectedQuantity,
  labels,
  exchangeTo,
  locale,
  includeCurrency,
}: {
  item: ExtendedOrderItem | ExtendedReturnItem;
  itemDisplay?: Maybe<ItemDisplay>;
  selectedQuantity?: number;
  labels?: {
    skuLabel?: string;
    priceLabel?: string;
    qtyLabel?: string;
    sizeLabel?: string;
    colorLabel?: string;
  };
  exchangeTo?: ExchangeVariant;
  locale?: string;
  includeCurrency?: boolean;
}) => {
  const { additionalItemAttributeConfig, itemAttributeConfig } =
    itemDisplay || {};

  /**
   * In order to order and potential hide any attributes, we leverage a config array that
   * includes allowed keys in a desired order.
   *
   * itemAttributeConfig - A prop that comes from itemDisplay via useSettings() and is set in Quin retailer settings for returns.
   * In the absence of itemAttributeConfig, we use a default config DEFAULT_ITEM_ATTR_CONFIG.
   */
  const ITEM_ATTR_CONFIG = itemAttributeConfig ?? DEFAULT_ITEM_ATTR_CONFIG;

  const variantInfo = "variantInfo" in item && item.variantInfo;
  const hasVariantInfo = !!variantInfo;

  // We also need to set up a config array for the variant attributes as they have their own ordering logic.
  const VARIANT_ATTR_CONFIG = hasVariantInfo
    ? Object.entries(variantInfo)
        // First sort the variant entries
        .sort((a, b) => {
          const entriesLength = Object.entries(variantInfo).length;
          return (
            (a[1].optionPosition ?? entriesLength) -
            (b[1].optionPosition ?? entriesLength)
          );
        })
        // Next reduce the entries to an array of keys.
        .reduce((acc, variantEntry) => {
          acc.push(variantEntry[0]);
          return acc;
        }, [] as string[])
    : [];

  // We want to avoid the duplication of variant keys in the ITEM_ATTR_CONFIG.
  // Variant keys trump ITEM_ATTR_CONFIG keys, so we throw away duplicates from ITEM_ATTR_CONFIG.
  const REDUCED_ITEM_ATTR_CONFIG = ITEM_ATTR_CONFIG.reduce((acc, key) => {
    if (key && !VARIANT_ATTR_CONFIG.includes(key)) {
      acc.push(key);
    }
    return acc;
  }, [] as string[]);

  // Next we need to produce a final DISPLAY_ITEM_ATTR_CONFIG, spreading the VARIANT_ATTR_CONFIG into
  // the index of variantInfo in the REDUCED_ITEM_ATTR_CONFIG.
  const variantInfoIndex = REDUCED_ITEM_ATTR_CONFIG.indexOf("variantInfo");
  const DISPLAY_ITEM_ATTR_CONFIG =
    variantInfoIndex >= 0
      ? [
          ...REDUCED_ITEM_ATTR_CONFIG.slice(0, variantInfoIndex),
          ...VARIANT_ATTR_CONFIG,
          ...REDUCED_ITEM_ATTR_CONFIG.slice(variantInfoIndex + 1),
        ]
      : [...REDUCED_ITEM_ATTR_CONFIG];

  // Finally, we begin the collection of attributes for display, by first producing slots
  // for each attribute to be placed. Empty slots will be removed at the end.
  const attributes = Array<Attribute>(DISPLAY_ITEM_ATTR_CONFIG.length);

  // QUANTITY
  const displayQuantity = selectedQuantity ? selectedQuantity : item.quantity;
  const hasQuantity = displayQuantity > 0;
  const quantityIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf("quantity");
  const includeQuantity = hasQuantity && quantityIndex >= 0;
  if (includeQuantity) {
    attributes[quantityIndex] = {
      key: labels?.qtyLabel,
      value: displayQuantity,
    };
  }

  // VARIANT INFO
  // These booleans are used to track whether color or size are included via variant info.
  let colorIncludedInVariant = false;
  let sizeIncludedInVariant = false;

  // When checking whether to include the variantInfo, we need to check the REDUCED_ITEM_ATTR_CONFIG, and
  // not the DISPLAY_ITEM_ATTR_CONFIG, as the variantInfo has already been merged in
  if (hasVariantInfo && REDUCED_ITEM_ATTR_CONFIG.includes("variantInfo")) {
    const variantInfoEntries = Object.entries(variantInfo);
    variantInfoEntries.forEach(([key, value]) => {
      const index = DISPLAY_ITEM_ATTR_CONFIG.indexOf(key);
      if (
        !exchangeTo?.exchangeForCredit &&
        exchangeTo?.variantInfo?.[key] &&
        exchangeTo?.variantInfo?.[key].value !== value.value
      ) {
        attributes[index] = {
          key: `${value.optionName ?? value.optionId}: `,
          transaction: {
            before: value.name ?? value.value,
            after:
              exchangeTo?.variantInfo?.[key]?.name ??
              exchangeTo?.variantInfo?.[key]?.value,
          },
        };
      } else {
        attributes[index] = {
          key: `${value.optionName ?? value.optionId}: `,
          value: value.name ?? value.value,
        };
      }

      // If color or size are included in the variant info, we track them in the provided booleans.
      if (key === "color") colorIncludedInVariant = true;
      if (key === "size") sizeIncludedInVariant = true;
    });
  }

  // COLOR IF NOT INCLUDED IN VARIANT INFO
  const colorIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf("color");
  if (!colorIncludedInVariant && item.color && colorIndex >= 0) {
    attributes[colorIndex] = {
      key: labels?.colorLabel,
      value: item.color,
    };
  }

  // SIZE IF NOT INCLUDED IN VARIANT INFO
  const sizeIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf("size");
  if (!sizeIncludedInVariant && item.size && sizeIndex >= 0) {
    attributes[sizeIndex] = {
      key: labels?.sizeLabel,
      value: item.size,
    };
  }

  // SKU
  const sku = exchangeTo?.sku ?? item.sku;
  const skuIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf("sku");
  if (sku && skuIndex >= 0) {
    attributes[skuIndex] = {
      key: labels?.skuLabel,
      value: sku,
    };
  }

  // PRICE
  const priceIndex = DISPLAY_ITEM_ATTR_CONFIG.indexOf("price");
  if (priceIndex >= 0) {
    if (typeof item.price?.value === "number") {
      let priceItem;
      if (
        !exchangeTo?.exchangeForCredit &&
        typeof exchangeTo?.price.value === "number" &&
        exchangeTo.price.value !== item.price.value
      ) {
        priceItem = {
          key: labels?.priceLabel,
          transaction: {
            before: (
              <Currency
                locale={locale}
                includeCurrency={includeCurrency}
                value={item.price.value}
                currency={item.price.currency}
              />
            ),
            after: (
              <Currency
                locale={locale}
                includeCurrency={includeCurrency}
                value={exchangeTo.price.value}
                currency={exchangeTo.price.currency}
              />
            ),
          },
        };
      } else if (
        "discountedPrice" in item &&
        typeof item.discountedPrice?.value === "number"
      ) {
        priceItem = {
          key: labels?.priceLabel,
          transaction: {
            before: (
              <Currency
                locale={locale}
                includeCurrency={includeCurrency}
                value={item.price.value}
                currency={item.price.currency}
              />
            ),
            after: (
              <Currency
                locale={locale}
                includeCurrency={includeCurrency}
                value={item.discountedPrice.value}
                currency={item.discountedPrice.currency}
              />
            ),
          },
        };
      } else {
        priceItem = {
          key: labels?.priceLabel,
          value: (
            <Currency
              locale={locale}
              includeCurrency={includeCurrency}
              value={item.price.value}
              currency={item.price.currency}
            />
          ),
        };
      }

      attributes[priceIndex] = priceItem;
    }
  }

  // ADDITIONAL ATTRIBUTES
  // additionalProperties are passed in via the item and need display.
  // NOTE: They will only be displayed if configured via itemDisplay.itemAttributeConfig in Quin.
  const additionalItemAttrs = getAdditionalItemAttrs(item.additionalProperties);

  if (additionalItemAttrs) {
    Object.keys(additionalItemAttrs).forEach((key) => {
      const index = DISPLAY_ITEM_ATTR_CONFIG.indexOf(key);
      if (index >= 0) {
        // To find the current additional attribute, we need to find the object with the attribute key that matches the current key.
        const thisAdditionalItemAttributeConfig =
          additionalItemAttributeConfig?.find((item) => item.attribute === key);

        // If there is a custom label in the this attribute config, we use it, otherwise we use the label from the additionalItemAttrs object which comes from the retailer.
        const label =
          thisAdditionalItemAttributeConfig?.value?.label ??
          additionalItemAttrs[key].label;

        // Then we create a key value pair for rendering the attribute.  If configured, we transform the value using getTransformedAttribute.
        attributes[index] = {
          key: `${label}: `, // We add colon and space to the label.
          value: getTransformedAttribute(
            thisAdditionalItemAttributeConfig?.value?.transform ?? "none",
            {
              key,
              additionalItemAttrs,
              additionalItemAttributeConfig,
              includeCurrency,
              locale,
            },
          ),
        };
      }
    });
  }

  // Return the filtered attributes to remove any empty slots in the original attributes array.
  // Empty slots can occur if a given attributes doesn't pass a give check.  e.g. quantity is 0
  return attributes.filter((n) => n != null);
};

export const getItemProps = ({
  item,
  exchangeTo,
  isClaim,
}: {
  item: ExtendedOrderItem;
  exchangeTo?: ExchangeVariant;
  isClaim?: boolean;
}) => {
  const returnType: ReturnReasonsReturnType = isClaim
    ? "claim"
    : exchangeTo && exchangeTo.exchangeForCredit
      ? "exchangeForCredit"
      : exchangeTo
        ? "exchange"
        : "return";
  const title =
    returnType === "return"
      ? item.name
      : (exchangeTo?.name ??
        exchangeTo?.variantInfo?.product?.name ??
        item.name);
  const thumbnail =
    returnType === "exchange"
      ? { old: item.imageUrl, new: exchangeTo?.imageUrl }
      : item.imageUrl;

  return {
    thumbnail,
    title,
    returnType,
  };
};

export const ITEM_CARD_LABELS_TRANSLATION_TOKENS: (keyof RetailerTranslations)[] =
  [
    "returnsItemListItemInfoskuLabel",
    "returnsItemListItemInfopriceLabel",
    "returnsItemListItemInfoqtyLabel",
    "returnsItemListItemInfocolorLabel",
    "returnsItemListItemInfosizeLabel",
    "returnsItemListItemInforeasonLabel",
  ];

export const useItemCardLabels = () => {
  const { rawValue: skuLabel } = useLanguage({
    langKey: "returnsItemListItemInfoskuLabel",
  });

  const { rawValue: priceLabel } = useLanguage({
    langKey: "returnsItemListItemInfopriceLabel",
  });

  const { rawValue: qtyLabel } = useLanguage({
    langKey: "returnsItemListItemInfoqtyLabel",
  });

  const { rawValue: colorLabel } = useLanguage({
    langKey: "returnsItemListItemInfocolorLabel",
  });

  const { rawValue: sizeLabel } = useLanguage({
    langKey: "returnsItemListItemInfosizeLabel",
  });

  const { rawValue: reasonLabel } = useLanguage({
    langKey: "returnsItemListItemInforeasonLabel",
  });

  return {
    skuLabel,
    priceLabel,
    qtyLabel,
    colorLabel,
    sizeLabel,
    reasonLabel,
  };
};
